import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AddEditHighDemandDate } from 'screens/Authenticated/Configuration/Venue/AddEditVenue/AddEditHighDemandDate/AddEditHighDemandDate';
import { fetchFromStorage, saveToStorage } from 'utils/storage';
import strings from '../../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../../utils/axios';
import { URLS } from '../../../../../../utils/constants/urls';
import {
  getOrganizationList,
  languages,
  refreshToken,
} from '../../../../../../utils/redux/reducer/authentication-slice';
import AddEdit from '../../../../CommonPage/AddEdit';
import { AddEditOrganizationForm } from '../AddEditOrganizationForm';
import { FormDataModel, FormModel } from 'components/Form';
import { CountryCode } from 'libphonenumber-js';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import { Box } from '@mui/material';
import { routes } from 'utils/constants/routes';
import { identifiers } from 'utils/constants/identifiers';
import PageLoader from 'components/PageLoader';
import {
  ConfigPaymentScheduleSection,
  ConfigPaymentScheduleValues,
  pickConfigPaymentScheduleValues,
  validateConfigPaymentSchedule,
} from 'components/ConfigPaymentScheduleSection';

type GeneralTabProps = {
  setTabValue: any;
  pageResponse: any;
  setPageResponse: any;
  dropdownValue: any;
  initialOrg: any;
};

const emptySchedule = (): ConfigPaymentScheduleValues => ({
  minConfirmPaymentPercentage: '',
  paymentSlotOnePercentage: '',
  paymentSlotTwoPercentage: '',
  paymentSlotThreePercentage: '',
  paymentSlotOneBeforeDays: '',
  paymentSlotTwoBeforeDays: '',
  fullPaymentBeforeDays: '',
});

export const GeneralTab = ({
  setTabValue,
  pageResponse,
  setPageResponse,
  dropdownValue,
  initialOrg,
}: GeneralTabProps) => {
  const languageList = useSelector(languages) as any;
  const org = fetchFromStorage(identifiers.organization) as any;
  const defaultCountry = (fetchFromStorage('defaultCountryCode') || 'IN') as CountryCode;
  const { id } = useParams() as any;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [highDemandDate, setHighDemandDate] = useState<any>([]);
  const [currencyList, setCurrencyList] = useState<any>([]);
  const [customId, setCustomId] = useState<any>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<ConfigPaymentScheduleValues>(emptySchedule);
  const [scheduleHasError, setScheduleHasError] = useState(false);
  const [scheduleErrors, setScheduleErrors] = useState<Partial<Record<keyof ConfigPaymentScheduleValues, string>>>(
    {},
  );

  useEffect(() => {
    if (pageResponse) {
      setSchedule(pickConfigPaymentScheduleValues(pageResponse));
      setScheduleHasError(false);
      setScheduleErrors({});
    }
  }, [pageResponse]);

  const handleScheduleChange = (field: keyof ConfigPaymentScheduleValues, value: unknown) => {
    setSchedule(prev => ({ ...prev, [field]: value }));
    if (scheduleHasError) {
      setScheduleErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSetModel = useCallback(() => {
    const newArray = [...AddEditOrganizationForm(currencyList, defaultCountry)] as FormModel[];
    languageList !== undefined &&
      languageList?.forEach((languae: any) => {
        const newObject = { ...AddEditOrganizationForm(currencyList, defaultCountry)[1] };
        newObject.field = 'name_' + languae.value;
        newObject.label = newObject.label + ' (' + languae.text + ')';
        newArray.splice(1, 0, newObject);
      });

    const filtered = newArray.filter((item: any) => item.field !== 'name');
    const sectionIndex = filtered.findIndex((item: any) => item.field === 'organization_section3');
    const scheduleField: FormModel = {
      label: '',
      value: '',
      type: 'custom',
      placeholder: '',
      field: 'configPaymentSchedule',
      validators: [],
      required: false,
      responsive: { xs: 12 },
      children: (
        <ConfigPaymentScheduleSection
          values={schedule}
          onChange={handleScheduleChange}
          hasError={scheduleHasError}
          errors={scheduleErrors}
        />
      ),
    };

    if (sectionIndex >= 0) {
      filtered.splice(sectionIndex + 1, 0, scheduleField);
    } else {
      filtered.push(scheduleField);
    }
    return filtered;
  }, [currencyList, defaultCountry, languageList, schedule, scheduleHasError, scheduleErrors]);

  const fetchCurrency = async () => {
    const { data, status } = await axiosInstance.get(URLS.organizationCurrency);
    if (status === 200) {
      setCurrencyList(createDropdownData(data, ['id', 'name']));
    }
  };
  useEffect(() => {
    fetchCurrency();
  }, []);

  const getPageData = useCallback(async () => {
    try {
      setLoading(true);
      const pageId = id;
      const { data } = await axiosInstance.get(URLS.getOrgGeneralById(pageId));
      const container: any = {};
      for (let i = 0; i < languageList?.length; ++i) {
        let values = ['name_' + languageList[i]?.value];
        container[values as any] = data.localizedAttributes[languageList[i]?.value]
          ? data.localizedAttributes[languageList[i]?.value].name
          : '';
      }
      setHighDemandDate(data.highDemandDates);
      setPageResponse({
        ...data,
        ...container,
        showAlternativeDates: data?.showAlternativeDates
          ? { label: strings.dropdown_yes_text, value: data?.showAlternativeDates }
          : { label: strings.dropdown_no_text, value: false },
        showRFQForExceedingBookingWindowTimeFrame: data?.showRFQForExceedingBookingWindowTimeFrame
          ? { label: strings.dropdown_yes_text, value: data?.showRFQForExceedingBookingWindowTimeFrame }
          : { label: strings.dropdown_no_text, value: false },
      });
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getPageData();
      }
    })();
  }, [getPageData, id]);

  const pageConfig = useMemo(
    () => ({
      title: id ? strings.organization_text_edit_org : strings.organization_text_add_org,
      successMessage: id ? strings.common_update_message : strings.common_save_message,
      addEditApiUrl: id ? URLS.getOrgGeneralById(id) : URLS.addOrganization,
      formModel: handleSetModel(),
      navigateToPage: '',
      isCustomResponse: true,
      isFormData: false,
      initialOrgNull: initialOrg === '*',
      hideClose: true,
    }),
    [id, handleSetModel, initialOrg],
  );

  const handleCustomResponse = (custom: any) => {
    const validation = validateConfigPaymentSchedule(schedule, { requirePercents: true });
    if (!validation.valid) {
      setScheduleHasError(true);
      setScheduleErrors(validation.errors);
      return false as any;
    }

    setScheduleHasError(false);
    setScheduleErrors({});

    const localizedAttributes = [] as any;
    languageList !== undefined &&
      languageList?.map((item: { text: string; value: string }) =>
        localizedAttributes.push({
          language: item.value,
          name: custom['name_' + item.value],
        }),
      );
    const emptyToNull = (value: unknown) => (value === '' || value == null ? null : value);
    const body = {
      ...custom,
      minConfirmPaymentPercentage: emptyToNull(schedule.minConfirmPaymentPercentage),
      paymentSlotOnePercentage: emptyToNull(schedule.paymentSlotOnePercentage),
      paymentSlotTwoPercentage: emptyToNull(schedule.paymentSlotTwoPercentage),
      paymentSlotThreePercentage: emptyToNull(schedule.paymentSlotThreePercentage),
      paymentSlotOneBeforeDays: emptyToNull(schedule.paymentSlotOneBeforeDays),
      paymentSlotTwoBeforeDays: emptyToNull(schedule.paymentSlotTwoBeforeDays),
      fullPaymentBeforeDays: emptyToNull(schedule.fullPaymentBeforeDays),
      orderPrefix: custom.orderPrefix?.trim().toUpperCase(),
      roomsAvailable: custom.roomsAvailable.value,
      bookingConfirmationBy: custom.bookingConfirmationBy.value,
      showAlternativeDates: custom.showAlternativeDates.value,
      alternativeDateInterval: parseInt(custom.alternativeDateInterval),
      bookingWindowTimeFrame: parseInt(custom.bookingWindowTimeFrame),
      showRFQForExceedingBookingWindowTimeFrame: custom.showRFQForExceedingBookingWindowTimeFrame.value,
      locationId: custom.location.value,
      localizedAttributes: localizedAttributes,
    };

    return body;
  };
  const handleFormChange = (key: string, value: any, formData: any, isDeleted: any, formRef: any, setHasError: any) => {
    if (key === 'showAlternativeDates') {
      if (value?.value) {
        const { getFormData } = formRef.current as {
          getFormData: () => { formData: FormDataModel; isFormValid: boolean };
        };
        const { formData } = getFormData();
        if (formData.alternativeDateInterval === '') {
          formRef.current?.handleChange('', 'alternativeDateInterval', { error: true, message: strings.requiredText });
          setHasError(true);
        }
      } else {
        formRef.current?.handleChange('', 'alternativeDateInterval', { error: false, message: '' });
      }
    }
  };
  useEffect(() => {
    if (org !== '*' && customId !== undefined) {
      setTabValue(1);
      saveToStorage('orgTabValue', '1');
      navigate(`${routes.editOrganization}/${customId}`, { replace: true });
    }
  }, [org, customId]);

  const customCallbackResponse = (data: any) => {
    handleHighDemandDate(data.id);
    if (initialOrg === '*') {
      dispatch(refreshToken() as any);
    }
    if (!id) {
      dispatch(getOrganizationList() as any);
    }
    setCustomId(data.id);
  };

  const handleHighDemandDate = async (id: number | string) => {
    await axiosInstance.put(URLS.updateHighDemandDate(id as string), { highDemandDates: highDemandDate || [] });
  };

  return (
    <Box py={1}>
      {loading && <PageLoader />}
      <AddEdit
        customCallbackResponse={customCallbackResponse}
        handlePropsChange={handleFormChange}
        dropdownValue={dropdownValue}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        handleCustomResponse={handleCustomResponse as any}>
        <hr />
        <AddEditHighDemandDate editedData={highDemandDate} setData={setHighDemandDate} />
      </AddEdit>
    </Box>
  );
};
